import React, { useState } from 'react';
import { formatDublinDate, formatDublinTime } from '../../utils/date';
import { useParams } from 'react-router-dom';
import { useListFireDrillsQuery } from '../../store/api/localApi';
import { FireDrill, FireDrillScope, FireDrillType } from '../../types';
import StartDrillModal from '../../components/fire-drills/StartDrillModal';
import { useLocationContext } from '../../hooks/useLocationContext';
import { useAuth } from '../../hooks/useAuth';
import { ScopedLink } from '../../components/ScopedLink';
import { getChipConfig } from '../../utils/complianceChip';

// ─── helpers ─────────────────────────────────────────────────────────────────

function formatEvacuationTime(seconds: number | undefined): string {
  if (seconds === undefined || seconds === null) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function labelForType(type: FireDrillType): string {
  switch (type) {
    case FireDrillType.ANNOUNCED:
      return 'Announced';
    case FireDrillType.UNANNOUNCED:
      return 'Unannounced';
    case FireDrillType.EVAC_REAL:
      return 'Real evac';
  }
}

// Dublin wall clock, not UTC (an hour off all summer) — see FireDrillDetailPage.
function formatDateTime(isoString: string): { date: string; time: string } {
  return { date: formatDublinDate(isoString), time: formatDublinTime(isoString) };
}

function issueText(drill: FireDrill): string {
  const open = drill.issues.filter((i) => !i.resolvedAt).length;
  const closed = drill.issues.filter((i) => i.resolvedAt).length;
  if (open === 0 && closed === 0) return 'No issues';
  if (open === 0) return `${closed} issue${closed === 1 ? '' : 's'} ✓ resolved`;
  return `⚠ ${open} open issue${open === 1 ? '' : 's'}`;
}

// ─── DrillRow ─────────────────────────────────────────────────────────────────

interface DrillRowProps {
  drill: FireDrill;
}

const DrillRow: React.FC<DrillRowProps> = ({ drill }) => {
  const { date, time } = formatDateTime(drill.startedAt);
  const accountedFor = drill.attendees.filter((a) => a.accountedFor).length;
  const total = drill.attendees.length;
  const scopeLabel =
    drill.scope === FireDrillScope.LOCATION
      ? 'Location'
      : drill.className
      ? `Class: ${drill.className}`
      : 'Class';
  const issueLabel = issueText(drill);
  const hasOpenIssues = drill.issues.some((i) => !i.resolvedAt);

  return (
    <ScopedLink
      to={`/fire-drills/${drill.id}`}
      data-testid={`fire-drill-log-row-${drill.id}`}
      className="block px-4 py-3 hover:bg-gray-50 transition-colors"
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
        {/* Date · time */}
        <span className="text-gray-900 font-medium whitespace-nowrap">
          {date} · {time}
        </span>

        {/* Scope */}
        <span className="text-gray-600 whitespace-nowrap">{scopeLabel}</span>

        {/* Type */}
        <span className="text-gray-600 whitespace-nowrap">{labelForType(drill.type)}</span>

        {/* Evac time */}
        <span className="text-gray-600 whitespace-nowrap">
          {formatEvacuationTime(drill.evacuationSeconds)}
        </span>

        {/* Attendee ratio */}
        <span className="text-gray-600 whitespace-nowrap">
          {accountedFor}/{total}
        </span>

        {/* Recorder */}
        <span className="text-gray-500 whitespace-nowrap">{drill.startedByName}</span>

        {/* Issues */}
        <span
          className={`whitespace-nowrap ${
            hasOpenIssues ? 'text-amber-700 font-medium' : 'text-gray-400'
          }`}
        >
          {issueLabel}
        </span>
      </div>
    </ScopedLink>
  );
};

// ─── FireDrillLogPage ─────────────────────────────────────────────────────────

const FireDrillLogPage: React.FC = () => {
  // Source locationId from any of the route shapes we're registered under:
  //   /locations/:locationId/fire-drills      (flat — original)
  //   /loc/:locId/fire-drills                 (org-admin scoped into a loc)
  //   /org/:orgId/loc/:locId/fire-drills      (super-admin scoped into a loc)
  // LocationContext.activeLocationId is the canonical source when the user
  // is scoped via the URL prefix; the flat `:locationId` param wins when
  // someone hits the bare /locations/<id>/fire-drills URL directly.
  const params = useParams<{ locationId?: string; locId?: string }>();
  const { activeLocationId } = useLocationContext();
  const { isStaffOrAbove } = useAuth();
  const locationId = params.locationId || params.locId || activeLocationId || undefined;

  // Fire drills are a staff-and-above surface (the backend gates the list on
  // `location:read-staff`). A PARENT who lands here would fire a query that
  // 403s and see a misleading "Failed to load fire drills" — so guard before
  // firing, and skip the query for them entirely.
  const { data, isLoading, isError, refetch } = useListFireDrillsQuery(
    { locationId: locationId!, limit: 100 },
    { skip: !locationId || !isStaffOrAbove }
  );

  const [modalOpen, setModalOpen] = useState(false);

  if (!locationId) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">
        Invalid URL
      </div>
    );
  }

  // Parents (and anyone below STAFF) have no access to the fire-drill log. Show
  // a clean access-denied state rather than the raw "Failed to load" a 403
  // would otherwise produce.
  if (!isStaffOrAbove) {
    return (
      <div
        data-testid="fire-drill-log-denied"
        className="min-h-screen flex items-center justify-center text-sm text-slate-500"
      >
        You don&apos;t have access to this page.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 text-sm text-slate-500">
        <p className="text-base font-medium text-slate-700">Failed to load fire drills.</p>
        <button
          type="button"
          onClick={() => refetch()}
          className="text-primary-600 hover:text-primary-500 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  const cadence = data?.cadence;
  const drills = data?.drills ?? [];

  const chip = cadence ? getChipConfig(cadence.isCompliant, cadence.daysInMonthRemaining) : null;

  // Group drills by year (UTC year of startedAt)
  const drillsByYear = drills.reduce((acc, d) => {
    const year = new Date(d.startedAt).getUTCFullYear();
    (acc[year] = acc[year] ?? []).push(d);
    return acc;
  }, {} as Record<number, FireDrill[]>);

  const years = Object.keys(drillsByYear)
    .map(Number)
    .sort((a, b) => b - a);

  const renderStartDrillButton = (slot: 'header' | 'empty') => (
    <button
      type="button"
      // Rendered at two sites (header CTA + empty-state CTA). A single shared
      // testid put two identical ones in the DOM, which fails Playwright's
      // strict mode ("resolved to 2 elements") and violates the project rule
      // that every interactive element has a STABLE, UNIQUE testid.
      data-testid={`fire-drill-log-start-drill-${slot}`}
      onClick={() => setModalOpen(true)}
      className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
    >
      Start drill
    </button>
  );

  return (
    <div data-testid="fire-drill-log" className="max-w-4xl mx-auto px-4 py-8">
      {/* Top section: heading + chip + CTA */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
          <span aria-hidden="true">🔥</span>
          Fire Drill Log
        </h1>

        {chip ? (
          <span
            data-testid="fire-drill-log-cadence-chip"
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${chip.classes}`}
          >
            {chip.label}
          </span>
        ) : (
          <span
            data-testid="fire-drill-log-cadence-chip"
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-400"
          >
            No data
          </span>
        )}

        <div className="ml-auto">{renderStartDrillButton('header')}</div>
      </div>

      {/* Classes-not-drilled banner */}
      {cadence && cadence.classesNotDrilledThisMonth.length > 0 && (
        <div
          data-testid="fire-drill-log-classes-not-drilled-banner"
          className="mb-4 rounded-md bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800"
        >
          ⚠ Classes not drilled this month:{' '}
          <span className="font-medium">
            {cadence.classesNotDrilledThisMonth.map((c) => c.name).join(', ')}
          </span>
        </div>
      )}

      {/* Empty state */}
      {drills.length === 0 ? (
        <div className="mt-12 flex flex-col items-center gap-4 text-center">
          <p className="text-sm text-gray-500">No fire drills recorded yet.</p>
          {renderStartDrillButton('empty')}
        </div>
      ) : (
        /* Chronological list grouped by year */
        <div className="space-y-6">
          {years.map((year) => (
            <section key={year}>
              <h2 className="text-base font-semibold text-gray-700 mb-1 pb-1 border-b border-gray-200">
                {year}
              </h2>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 divide-y divide-gray-100">
                {/* Newest-first within each year */}
                {[...drillsByYear[year]]
                  .sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime())
                  .map((drill) => (
                    <DrillRow key={drill.id} drill={drill} />
                  ))}
              </div>
            </section>
          ))}
        </div>
      )}

      <StartDrillModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        locationId={locationId}
        recentDrills={drills.slice(0, 10)}
      />
    </div>
  );
};

export default FireDrillLogPage;
